import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { HttpClientModule } from '@angular/common/http';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { strictEmailValidator } from '../../shared/validators/email.validator';

/**
 * Form component for requesting a password reset link by entering an email address.
 * This standalone component renders a reactive form to collect the user's email, validates it
 * with a strict email validator, calls the AuthService.forgotPassword endpoint, and navigates
 * to a resend-email confirmation route on success. It also surfaces validation and server
 * error messages and manages a loading state while the request is in progress.
 * @component
 * @selector app-forgot-password-form
 * @example
 *   <app-forgot-password-form></app-forgot-password-form>
 * @input none - This component does not accept any @Input properties; it builds and manages its own FormGroup internally.
 * @output none - This component does not emit any @Output events; it performs navigation and message handling internally via injected services.
 *
 * Key methods:
 * - constructor(authService, router, messageService, formBuilder): Initializes the reactive form (email control) and injects required services.
 * - onSubmit(): Validates the form, sets errorMessage for client-side validation errors, toggles loading, calls AuthService.forgotPassword(email),
 *   handles success by navigating to '/resend-email' with the email as a query parameter, and handles API errors by setting errorMessage.
 * - getEmailErrorMessage(): Returns a user-facing validation message for the email field (required or strict email validator message) or null when valid.
 * - hasEmailError(): Returns a boolean indicating whether the email control is invalid and touched (used to apply error styling).
 *
 * Important lifecycle / behavior notes:
 * - The form is created in the constructor using FormBuilder and contains a single 'email' control with Validators.required and strictEmailValidator.
 * - No Angular lifecycle hooks (ngOnInit, ngOnDestroy, etc.) are implemented explicitly; asynchronous work is handled via RxJS subscriptions on the AuthService call.
 * - The component uses Router for navigation on success and NotificationService for potential message display (commented example present).
 * - Error handling surfaces server-provided messages when available and falls back to sensible defaults.
 */
@Component({
  selector: 'app-forgot-password-form',
  standalone: true,
  imports: [CommonModule, RouterLink, ReactiveFormsModule, HttpClientModule, ErrorMessageComponent],
  templateUrl: './forgot-password-form.component.html',
  styleUrl: './forgot-password-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AuthService],
})
export class ForgotPasswordFormComponent {
  forgotPasswordForm: FormGroup;
  errorMessage: string | null = null;
  emailSent = false;
  loading = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private messageService: NotificationService,
    private formBuilder: FormBuilder
  ) {
    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, strictEmailValidator()]],
    });
  }

  /**
   * Submit the forgot password form: validate inputs, set loading/error state, call the API and navigate on success.
   * @example
   * nSubmit()
   * undefined
   * @returns {void} Performs validation and triggers an asynchronous forgot-password request; does not return a value.
   */
  onSubmit() {
    // Clear previous error messages
    this.errorMessage = null;

    // Mark all fields as touched to trigger validation display
    this.forgotPasswordForm.markAllAsTouched();

    if (this.forgotPasswordForm.invalid) {
      const emailControl = this.forgotPasswordForm.get('email');
      if (emailControl?.errors) {
        if (emailControl.errors['required']) {
          this.errorMessage = 'Email address is required.';
        } else if (emailControl.errors['strictEmail']) {
          this.errorMessage = emailControl.errors['strictEmail'].message;
        }
      }
      return;
    }

    this.loading = true;
    const email = this.forgotPasswordForm.get('email')?.value;

    this.authService.forgotPassword(email).subscribe({
      next: response => {
        this.loading = false;
        if (response.status === true) {
          this.router.navigate(['/resend-email'], { queryParams: { email: email } });
        } else {
          this.errorMessage = response.message || 'An unexpected error occurred.';
        }
      },
      error: err => {
        this.loading = false;
        this.errorMessage = err.error?.message || 'An error occurred. Please try again.';
        // this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'An error occurred. Please try again.', life: 3000 });
      },
    });
  }

  /**
   * Get the validation error message for the email field
   */
  getEmailErrorMessage(): string | null {
    const emailControl = this.forgotPasswordForm.get('email');
    if (emailControl?.invalid && emailControl?.touched) {
      if (emailControl.errors?.['required']) {
        return 'Email address is required.';
      } else if (emailControl.errors?.['strictEmail']) {
        return emailControl.errors['strictEmail'].message;
      }
    }
    return null;
  }

  /**
   * Check if the email field has an error and should show error styling
   */
  hasEmailError(): boolean {
    const emailControl = this.forgotPasswordForm.get('email');
    return !!(emailControl?.invalid && emailControl?.touched);
  }
}
